import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import IntegrationInstructionsRoundedIcon from "@mui/icons-material/IntegrationInstructionsRounded";

const plans = [
  {
    name: "Launch",
    audience: "Pilot projects and focused rollouts",
    price: "₹45,000",
    cadence: "/ month",
    badge: "Best for pilots",
    tone: {
      glow: "rgba(37,99,235,0.18)",
      border: "rgba(37,99,235,0.24)",
      chipBg: "rgba(219,234,254,0.9)",
      chipColor: "#1D4ED8",
    },
    included: [
      "Up to 100 active devices",
      "Site surveys and commissioning workflows",
      "Asset-device binding and installation reports",
      "Mobile app for operators with offline sync",
      "Standard dashboards, alerts, and PDF reports",
      "Email support and implementation guidance",
    ],
  },
  {
    name: "Scale",
    audience: "Multi-site utility and industrial programs",
    price: "₹1,20,000",
    cadence: "/ month",
    badge: "Most popular",
    featured: true,
    tone: {
      glow: "rgba(13,148,136,0.22)",
      border: "rgba(13,148,136,0.28)",
      chipBg: "rgba(204,251,241,0.95)",
      chipColor: "#0F766E",
    },
    included: [
      "Up to 500 active devices",
      "Advanced survey templates and project-level controls",
      "Commissioning approvals, revoke flows, and lifecycle control",
      "Runtime device licensing and telemetry blocking",
      "Firmware package and deployment management",
      "Webhook integrations and role-based operations",
    ],
  },
  {
    name: "Enterprise",
    audience: "OEMs, infra platforms, and large fleets",
    price: "Custom",
    cadence: "",
    badge: "Tailored engagement",
    tone: {
      glow: "rgba(124,58,237,0.18)",
      border: "rgba(124,58,237,0.24)",
      chipBg: "rgba(237,233,254,0.92)",
      chipColor: "#7C3AED",
    },
    included: [
      "1000+ active devices and multi-tenant scale",
      "Dedicated manufacturing, OEM, and partner workflows",
      "Certificate-based MQTTS and secure deployment controls",
      "Custom reports, APIs, and integration workflows",
      "Commercial lifecycle, billing foundations, and licensing controls",
      "Priority support, solution architecture, and rollout advisory",
    ],
  },
];

const addOns = [
  {
    icon: <SensorsRoundedIcon fontSize="small" />,
    title: "Device Pack Expansion",
    description:
      "Additional active device slabs for fast rollout scaling without changing the core plan structure.",
  },
  {
    icon: <PrecisionManufacturingRoundedIcon fontSize="small" />,
    title: "Manufacturing Traceability",
    description:
      "Partner, product, order, and unit-level upstream visibility for OEM and deployment-heavy programs.",
  },
  {
    icon: <IntegrationInstructionsRoundedIcon fontSize="small" />,
    title: "Integration Pack",
    description:
      "Webhook integration, ERP/CRM connectivity, customer-specific workflows, and downstream event routing.",
  },
  {
    icon: <WorkspacePremiumRoundedIcon fontSize="small" />,
    title: "Managed Operations Layer",
    description:
      "Optional managed monitoring, firmware coordination, alert handling, and operator support services.",
  },
];

const matrix = [
  {
    feature: "Site surveys and configurable survey templates",
    launch: true,
    scale: true,
    enterprise: true,
  },
  {
    feature: "Mobile commissioning with barcode, GPS, and photo proof",
    launch: true,
    scale: true,
    enterprise: true,
  },
  {
    feature: "Admin approval and revoke flows",
    launch: false,
    scale: true,
    enterprise: true,
  },
  {
    feature: "Runtime device licensing and telemetry blocking",
    launch: false,
    scale: true,
    enterprise: true,
  },
  {
    feature: "Firmware packages and deployment workflows",
    launch: false,
    scale: true,
    enterprise: true,
  },
  {
    feature: "Manufacturing and upstream traceability",
    launch: false,
    scale: false,
    enterprise: true,
  },
  {
    feature: "Certificate-based MQTTS and advanced secure onboarding",
    launch: false,
    scale: false,
    enterprise: true,
  },
  {
    feature: "Custom integrations and enterprise rollout design",
    launch: false,
    scale: false,
    enterprise: true,
  },
];

const CheckMark = ({ enabled }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      borderRadius: "50%",
      backgroundColor: enabled ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.12)",
      color: enabled ? "#15803D" : "#94A3B8",
    }}
  >
    <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />
  </Box>
);

export default function PricingPage() {
  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <Box
        sx={{
          borderRadius: 6,
          p: { xs: 3, md: 5 },
          background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
        }}
      >
        <Stack spacing={2.25}>
          <Typography
            sx={{
              fontSize: { xs: 28  , md: 40},
              fontWeight: 700,
              letterSpacing: "-0.06em",
              lineHeight: 1.02,
              maxWidth: 900,
            }}
          >
            Pricing built for industrial IoT rollouts.
          </Typography>
          <Typography
            sx={{
              maxWidth: 980,
              color: "rgba(255,255,255,0.86)",
              fontSize: { xs: 16, md: 20 },
              lineHeight: 1.75,
            }}
          >
            TrackCoreData is positioned as a deployment-to-operations platform covering
            site survey, commissioning, device onboarding, telemetry, firmware,
            licensing, and field evidence. These plans are structured to support pilot,
            scale-up, and enterprise/OEM programs.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              sx={{
                px: 2.2,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                       whiteSpace: "nowrap",
                      color: "#082F49",
                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                    }}
            >
              Request Pricing Discussion
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderRadius: 99,
                px: 3.25,
                py: 1.25,
                textTransform: "none",
                fontWeight: 800,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.34)",
              }}
            >
              Compare Plans
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" },
          gap: 2.5,
        }}
      >
        {plans.map((plan) => (
          <Card
            key={plan.name}
            sx={{
              borderRadius: 5,
              border: `1px solid ${plan.tone.border}`,
              boxShadow: plan.featured
                ? `0 24px 52px ${plan.tone.glow}`
                : "0 16px 36px rgba(15,23,42,0.06)",
              background:
                plan.featured
                  ? "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(240,253,250,0.9) 100%)"
                  : "#fff",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {plan.featured ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 6,
                  background: "linear-gradient(90deg, #0F766E, #14B8A6)",
                }}
              />
            ) : null}
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Stack spacing={1}>
                  <Chip
                    label={plan.badge}
                    sx={{
                      width: "fit-content",
                      bgcolor: plan.tone.chipBg,
                      color: plan.tone.chipColor,
                      fontWeight: 800,
                    }}
                  />
                  <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0F172A" }}>
                    {plan.name}
                  </Typography>
                  <Typography sx={{ color: "#64748B", lineHeight: 1.7 }}>
                    {plan.audience}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                  <Typography sx={{ fontSize: 40, fontWeight: 900, color: "#0F172A" }}>
                    {plan.price}
                  </Typography>
                  {plan.cadence ? (
                    <Typography sx={{ color: "#64748B", fontWeight: 700 }}>
                      {plan.cadence}
                    </Typography>
                  ) : null}
                </Stack>

                <Divider />

                <Stack spacing={1.15}>
                  {plan.included.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <CheckCircleRoundedIcon
                        sx={{ mt: "2px", fontSize: 18, color: "#16A34A", flexShrink: 0 }}
                      />
                      <Typography sx={{ color: "#334155", lineHeight: 1.65 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Button
                  fullWidth
                  variant={plan.featured ? "contained" : "outlined"}
                  sx={{
                    mt: 1,
                    borderRadius: 99,
                    textTransform: "none",
                    fontWeight: 800,
                    py: 1.15,
                    ...(plan.featured
                      ? {
                          backgroundColor: "#0F766E",
                          "&:hover": { backgroundColor: "#0D9488" },
                        }
                      : {}),
                  }}
                >
                  {plan.name === "Enterprise" ? "Talk to Us" : "Choose Plan"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.9fr" },
          gap: 2.5,
        }}
      >
        <Card
          sx={{
            borderRadius: 5,
            border: "1px solid rgba(15,23,42,0.08)",
            boxShadow: "0 16px 40px rgba(15,23,42,0.06)",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BoltRoundedIcon sx={{ color: "#2563EB" }} />
                <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0F172A" }}>
                  Plan Comparison
                </Typography>
              </Stack>
              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: 760 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2.2fr repeat(3, 1fr)",
                      gap: 1,
                      pb: 1,
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, color: "#64748B", px: 1 }}>
                      Capability
                    </Typography>
                    {["Launch", "Scale", "Enterprise"].map((label) => (
                      <Typography
                        key={label}
                        sx={{
                          textAlign: "center",
                          fontWeight: 800,
                          color: "#0F172A",
                        }}
                      >
                        {label}
                      </Typography>
                    ))}
                  </Box>
                  <Divider />
                  {matrix.map((row) => (
                    <Box
                      key={row.feature}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2.2fr repeat(3, 1fr)",
                        gap: 1,
                        alignItems: "center",
                        py: 1.2,
                        borderBottom: "1px solid rgba(226,232,240,0.8)",
                      }}
                    >
                      <Typography sx={{ px: 1, color: "#334155", lineHeight: 1.55 }}>
                        {row.feature}
                      </Typography>
                      <Box sx={{ textAlign: "center" }}>
                        <CheckMark enabled={row.launch} />
                      </Box>
                      <Box sx={{ textAlign: "center" }}>
                        <CheckMark enabled={row.scale} />
                      </Box>
                      <Box sx={{ textAlign: "center" }}>
                        <CheckMark enabled={row.enterprise} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: 5,
            border: "1px solid rgba(15,23,42,0.08)",
            boxShadow: "0 16px 40px rgba(15,23,42,0.06)",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={2.25}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BusinessCenterRoundedIcon sx={{ color: "#7C3AED" }} />
                <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0F172A" }}>
                  Commercial Add-ons
                </Typography>
              </Stack>
              {addOns.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    p: 2,
                    borderRadius: 3.5,
                    border: "1px solid rgba(15,23,42,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(124,58,237,0.12)",
                        color: "#7C3AED",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ mt: 0.5, color: "#64748B", lineHeight: 1.7 }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Card
        sx={{
          borderRadius: 5,
          border: "1px solid rgba(15,23,42,0.08)",
          background: "linear-gradient(135deg, #0F172A, #134E4A)",
          color: "#fff",
          boxShadow: "0 20px 48px rgba(15,23,42,0.14)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            spacing={2}
            alignItems={{ xs: "flex-start", lg: "center" }}
          >
            <Box sx={{ maxWidth: 880 }}>
              <Typography sx={{ fontSize: { xs: 20, md: 28}, fontWeight: 600, letterSpacing: "-0.04em" }}>
                Recommended commercial packaging
              </Typography>
              <Typography
                sx={{
                  mt: 1.25,
                  color: "rgba(255,255,255,0.82)",
                  lineHeight: 1.8,
                }}
              >
                Use a hybrid model: annual platform subscription + implementation fee +
                optional managed operations. This matches the product’s real strength in
                field deployment governance, lifecycle control, and operational reporting.
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Button
                variant="contained"
                sx={{
                  borderRadius: 99,
                      textTransform: "none",
                      fontWeight: 700,
                       whiteSpace: "nowrap",
                  bgcolor: "#FBBF24",
                  color: "#0F172A",
                  "&:hover": { bgcolor: "#F59E0B" },
                }}
              >
                Build Proposal
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: 99,
                  px: 3,
                  py: 1.1,
                  textTransform: "none",
                  fontWeight: 700,
                       whiteSpace: "nowrap",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.3)",
                }}
              >
                Share Pricing
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
